import type { Role } from './permissions';
import { effectiveRole, ROLE_LABEL } from './permissions';
import { displayName } from './people';
import type { Profiles, Access, Roles, ProjectRoles } from './people';
import type {
  Signatory,
  SignoffPolicy,
  SignoffRole,
  SignoffSlots,
  SignoffStage,
  SignoffStageMember,
  SignoffStageMode,
  SignoffStep,
  StoredSignoffConfig,
  StoredSignoffPolicy,
} from './types';

/**
 * ── WHO SIGNS IS A RULE, NOT A LIST OF NAMES — #332 ─────────────────────────
 *
 * A board used to store four typed names and positions, and that is the whole
 * problem Vinnie named on 1 September 2026: *"seeing that can change at any
 * time due to changing staff"*. A list of names is wrong the day somebody
 * leaves, and it is wrong silently — the sheet still looks complete, addressed
 * to a person who no longer works here.
 *
 * So a board stores a RULE. "The project owner, and any workspace admin." The
 * rule is turned into actual people at the MOMENT a sign-off is raised, so a
 * new manager is included without anybody maintaining anything.
 *
 * ⚠️ AND THE RESULT IS FROZEN ONTO THE SIGN-OFF, which is the other half and
 * matters more. An auditor reading last March's approval must see who it
 * ACTUALLY went to, not who would be asked today. That freezing happens in the
 * store, by copying these rows onto `signoffs`; this file only ever answers
 * "who, right now" and knows nothing about storage.
 *
 * TYPED NAMES SURVIVE ALONGSIDE, deliberately. Vinnie's decision the same day:
 * *"Roles, plus named outsiders."* A client's sponsor has no account here and
 * never will, which is why `Signatory.email` was optional long before this.
 */

/* The shape is in types.ts, beside Signatory — see the note there for why. */
export type {
  SignoffPolicy,
  SignoffRole,
  SignoffStage,
  SignoffStageMember,
  SignoffStageMode,
  SignoffStep,
  SignoffStepRule,
  StoredSignoffConfig,
  StoredSignoffPolicy,
} from './types';

export const SIGNOFF_ROLES: SignoffRole[] = ['owner', 'manager', 'admin'];

export const SIGNOFF_ROLE_LABEL: Record<SignoffRole, string> = {
  owner: 'The project owner',
  manager: ROLE_LABEL.manager + 's on this board',
  admin: 'Workspace admins',
};

export const SIGNOFF_ROLE_BLURB: Record<SignoffRole, string> = {
  owner: 'Whoever is set as the owner of this board, in Settings.',
  manager: 'Everybody who is a project manager here, however they got the rank.',
  admin: 'Everybody who can administer the workspace.',
};

/**
 * ── THE CHAIN, IN THE ORDER IT IS ASKED — #346 ──────────────────────────────
 *
 * Vinnie, 4 September 2026: the tester signs off, then the manager, then the
 * project owner, and only then does it go live. Two things he said the same day
 * shape this list. First, *"a test script is written and sent to the tester to
 * follow"* — writing it and sending it are STATES the work is in, not
 * signatures, so neither is a step here: nobody signs to say a document exists.
 * Second, *"not all types of approvals may be required all the time"* — so a
 * board turns any of them off, and a board that turns them all off is the board
 * everybody has today.
 *
 * ⚠️ THESE ARE NOT THE SAME THING AS THE RANKS ABOVE, though two of the words
 * are shared, and the difference is worth being slow about. A STEP is a stage
 * of the process — when this signature is asked for. A RANK is who answers it.
 * The manager step is very often answered by the manager rank and there is no
 * rule that it must be: a board can perfectly well have the project owner
 * answer the manager step, and saying so is the point of keeping the two apart.
 */
export const SIGNOFF_STEPS: Exclude<SignoffStep, ''>[] = ['uat', 'manager', 'owner'];

export const SIGNOFF_STEP_LABEL: Record<SignoffStep, string> = {
  '': 'Approval',
  uat: 'UAT sign-off',
  manager: "The manager's approval",
  owner: "The project owner's approval",
};

export const SIGNOFF_STEP_BLURB: Record<SignoffStep, string> = {
  '': 'One approval, asked of everybody at once.',
  uat: 'The tester, once they have been through the test script.',
  manager: 'Asked only after the tester has answered.',
  owner: 'The last word before it goes live.',
};

/**
 * ── THE LABEL FOR ONE SLICE OF A SHEET — configurable sign-offs R1 ────────────
 *
 * A sheet is grouped by `step`. On the legacy models that is one of the fixed
 * vocabulary ('', uat, manager, owner), named from `SIGNOFF_STEP_LABEL`. On the
 * stage model `step` is a board-defined stage id, and its label is found by
 * searching the WHOLE stored config — the default's stages AND every override's
 * stages — for a stage whose id matches. Override stage ids are unique, so a
 * global id search resolves the exact stage without needing to know the subject
 * type the sheet was raised against.
 *
 * ⚠️ RESOLVED LIVE, NEVER FROZEN. The row carries only the stage id (`step`) and
 * the frozen order (`stageSeq`); the label is read from the policy every time. A
 * stage since renamed shows its new name. A stage since REMOVED cannot be named,
 * so a neutral "Sign-off" is returned rather than the raw id — a sheet must
 * never show a machine id where a person expects a label.
 */
export function stepLabel(
  step: SignoffStep | string,
  policy: StoredSignoffPolicy | StoredSignoffConfig | undefined | null
): string {
  if (Object.prototype.hasOwnProperty.call(SIGNOFF_STEP_LABEL, step))
    return SIGNOFF_STEP_LABEL[step as SignoffStep];
  const { default: def, overrides } = readConfig(policy);
  for (const p of [def, ...Object.values(overrides)]) {
    if (!Array.isArray(p.stages)) continue;
    for (const s of p.stages)
      if (s && typeof s === 'object' && tidy(s.id) === step) {
        const label = tidy(s.label);
        if (label) return label;
      }
  }
  return 'Sign-off';
}

/**
 * ── THE ORDERED GROUPS A RAISED SHEET FALLS INTO — configurable sign-offs R1 ──
 *
 * Read off the ROWS, never the board's rule, for the reason #346 gives: the rule
 * is what WOULD be asked and can be edited after a sheet is raised; the rows are
 * what WAS asked, and a sheet from March must not be redrawn under June's chain.
 *
 *   STAGE SHEET — any row carries a frozen `stageSeq`. The groups are the
 *     distinct stage ids present, ordered by that frozen rank, so a board that
 *     re-ordered its stages after the raise cannot re-order this sheet.
 *   LEGACY CHAIN — with `groupLegacyChain` on (the sprint sheet, #346): the fixed
 *     `SIGNOFF_STEPS` present, in their fixed order — byte-for-byte the old
 *     `stepsOn`. Off (the release and document sheets, which have always drawn
 *     flat): no groups, so those sheets keep rendering exactly as they did.
 *   PLAIN — a single unnamed approval (every row at step ''): no groups, so the
 *     sheet is drawn flat with no heading, exactly as it always was.
 */
export function sheetGroups(
  rows: { step?: SignoffStep | string; stageSeq?: number | null }[],
  groupLegacyChain = false
): (SignoffStep | string)[] {
  if (rows.some((r) => r.stageSeq != null)) {
    const seqByStep = new Map<string, number>();
    for (const r of rows) {
      if (r.stageSeq == null) continue;
      const s = r.step ?? '';
      if (!seqByStep.has(s)) seqByStep.set(s, r.stageSeq);
    }
    return [...seqByStep.keys()].sort((a, b) => seqByStep.get(a)! - seqByStep.get(b)!);
  }
  if (!groupLegacyChain) return [];
  return SIGNOFF_STEPS.filter((s) => rows.some((r) => (r.step ?? '') === s));
}

/** No rule at all: what every board has until somebody sets one up. */
export const NO_POLICY: SignoffPolicy = { steps: [], named: [], onSprintFinish: false };

/**
 * THE RULE AS THE BOARD HAS IT, READ INTO THE CHAIN — #346.
 *
 * ⚠️ NOTHING REWRITES A STORED RULE, and that is deliberate. Boards carry the
 * shape #332 wrote — `{roles, onSprintFinish}` — and a migration that rewrites
 * a rule in place is a migration that can rewrite it wrongly, on a column that
 * decides who is asked to approve a fortnight of work. So the old shape is READ
 * as what it always meant: one unnamed step, asking those ranks. A board only
 * gains steps when somebody sets them up on the screen.
 *
 * This is the one place either shape is understood. Everything else takes the
 * chain and never asks which shape it came from.
 */
export const NO_SLOTS: SignoffSlots = { developer: false, uat: false, approver: false, owner: false };

/** The default slots a board takes when it is first switched to the model —
 *  the shape the change request has always drawn: developer, tester, approver,
 *  and the owner optional (off until the board turns it on). */
export const DEFAULT_SLOTS: SignoffSlots = { developer: true, uat: true, approver: true, owner: false };

/** A stored slots object read into the four-boolean shape, or undefined when the
 *  board carries none — undefined is the switch that keeps the rank chain. */
function readSlots(from: StoredSignoffPolicy['slots']): SignoffSlots | undefined {
  if (!from || typeof from !== 'object') return undefined;
  return {
    developer: from.developer === true,
    uat: from.uat === true,
    approver: from.approver === true,
    owner: from.owner === true,
  };
}

/**
 * ── ONE STAGE MEMBER, READ AND VALIDATED — configurable sign-offs R1 ──────────
 *
 * Who-signs is a crown jewel, so a member is kept only when it is WHOLE for its
 * kind and dropped otherwise — never guessed into somebody it did not name:
 *
 *   role     — needs a rank this build knows (SIGNOFF_ROLES). An unknown rank is
 *              a rank this build cannot ask, exactly as readPolicy drops an
 *              unknown step, so the member goes rather than sitting as a gap.
 *   person   — needs a non-empty account reference to resolve against the roster.
 *   external — needs a name AND a position (the panel's own rule since #332): a
 *              row with only half of a person is not a person to address.
 *
 * Anything else — a missing or unknown kind — is not a member and is dropped.
 */
function readStageMember(m: SignoffStageMember | undefined | null): SignoffStageMember | undefined {
  if (!m || typeof m !== 'object') return undefined;
  if (m.kind === 'role')
    return SIGNOFF_ROLES.includes(m.role as SignoffRole) ? { kind: 'role', role: m.role } : undefined;
  if (m.kind === 'person') {
    const ref = tidy(m.personRef);
    return ref ? { kind: 'person', personRef: ref } : undefined;
  }
  if (m.kind === 'external') {
    const name = tidy(m.name);
    const position = tidy(m.position);
    if (!name || !position) return undefined;
    return { kind: 'external', name, position, email: tidy(m.email) };
  }
  return undefined;
}

/**
 * A stored mode read into the three shapes, clamped to something answerable.
 *
 * 'all' is the safe default and where anything unrecognised lands — a missing
 * mode, a typo, a quorum that is not a whole number, a quorum of nought or less:
 * a stage should count as passed only when EVERYONE it asks has answered, never
 * on fewer by accident. A quorum of one is 'any one of them'. A quorum that meets
 * or exceeds the members it could ever ask is 'all', because that is what it is.
 */
function readStageMode(mode: SignoffStageMode | undefined, memberCount: number): SignoffStageMode {
  if (mode === 'anyOne') return 'anyOne';
  if (mode && typeof mode === 'object' && typeof mode.quorum === 'number' && Number.isFinite(mode.quorum)) {
    const q = Math.floor(mode.quorum);
    if (q <= 0) return 'all';
    if (q === 1) return 'anyOne';
    if (memberCount > 0 && q >= memberCount) return 'all';
    return { quorum: q };
  }
  return 'all';
}

/**
 * ── THE STAGES A BOARD DEFINED, READ AND VALIDATED — configurable sign-offs R1 ─
 *
 * Malformed stages are dropped and malformed members within a surviving stage are
 * dropped, so what comes back is asking only people it can name. A stage is kept
 * when it has a non-empty id; a stage that survives with NO members is left in on
 * purpose — policyProblem must be able to say "this stage asks nobody" out loud,
 * the same way the rank chain says a step switched on asks nobody. Two stages
 * with one id would collide on `step` when frozen, so the first wins.
 */
/**
 * True when a string is a bare UUID — the shape `crypto.randomUUID()` gives a
 * stage its id. No person types this as a stage name, so a label of this shape is
 * the fingerprint of the old id-backfill bug and is healed away on read.
 */
const looksLikeStageId = (s: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

function readStages(from: SignoffStage[]): SignoffStage[] {
  const seen = new Set<string>();
  const out: SignoffStage[] = [];
  for (const s of from) {
    if (!s || typeof s !== 'object') continue;
    const id = tidy(s.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const members = (Array.isArray(s.members) ? s.members : [])
      .map(readStageMember)
      .filter((m): m is SignoffStageMember => !!m);
    const order = Number(s.order);
    /*
     * The label is kept EXACTLY as typed, empty and all — never backfilled with
     * the id. An unnamed stage is drawn from its placeholder in the editor and
     * named "Sign-off" on a raised sheet (see `stepLabel`); backfilling the id
     * here put the raw UUID into the editable name field and every warning about
     * the stage (Vinnie, 30 September 2026). `stagesProblem` is what asks for the
     * missing name.
     *
     * ⚠️ AND IT HEALS DATA ALREADY SPOILT THAT WAY. The old backfill did not just
     * display the id — an edit after it (reorder, add a member) saved the stage
     * with its id sitting in the label, baking the UUID into storage. A stage
     * label that is a bare UUID was therefore never typed by a person (a real name
     * is "Development", not a 36-char hex string); it is that bug's fingerprint, so
     * it reads back as no-name and the next save clears it. We match the UUID SHAPE,
     * not just this stage's own id: overriding a work type copies the default's
     * stages with FRESH ids while keeping their labels (see SignoffRule.seedOverride),
     * so a default spoilt before this fix carried its old id into the override's
     * label, where it no longer equals the override stage's own id (Vinnie, 30 Sep
     * 2026 — the Test-script override still showed the raw UUID after the first fix).
     */
    const rawLabel = tidy(s.label);
    out.push({
      id,
      label: looksLikeStageId(rawLabel) ? '' : rawLabel,
      order: Number.isFinite(order) ? order : 0,
      members,
      mode: readStageMode(s.mode, members.length),
      ...(s.optional === true ? { optional: true } : {}),
    });
  }
  return out.sort((a, b) => a.order - b.order);
}

export function readPolicy(
  stored: StoredSignoffPolicy | StoredSignoffConfig | undefined | null
): SignoffPolicy {
  /* THE DEFAULT+OVERRIDES SHAPE IS READ AS ITS DEFAULT — configurable sign-offs
     R1. A board that has overridden a type stores `{default, overrides}`; asked
     for the board's policy (which is what every UI call site means), that is the
     default. A flat policy — every board that has never overridden — reads on
     down unchanged. `policyForType` is the one place an override is chosen. */
  const from = (isStoredConfig(stored) ? stored.default : stored) ?? {};
  const onSprintFinish = from.onSprintFinish === true;
  const slots = readSlots(from.slots);

  /*
   * STAGES FIRST, AND THAT ORDER IS THE BACK-COMPAT SWITCH — configurable
   * sign-offs R1. Presence of a non-empty `stages` array means the board is on
   * the stage model, exactly as `slots` presence means it is on the slots model.
   * It is tested BEFORE steps/roles/slots so a stage policy never falls through
   * into the steps filter (which would drop every stage id as an unknown step),
   * and it carries no slots or steps of its own: a board is on exactly one model.
   * A board with no `stages` reads on down and behaves precisely as it does today.
   */
  if (Array.isArray(from.stages) && from.stages.length)
    return { steps: [], named: [], onSprintFinish, stages: readStages(from.stages) };

  if (Array.isArray(from.steps))
    return {
      /* Anything the app does not recognise is dropped rather than carried:
         a step this build cannot name is a step it cannot ask, and leaving it
         in the list would put a gap in a chain nobody can see. */
      steps: from.steps
        .filter((s) => s && (s.step === '' || SIGNOFF_STEPS.includes(s.step as 'uat')))
        .map((s) => ({ step: s.step, roles: (s.roles ?? []).filter((r) => SIGNOFF_ROLES.includes(r)) })),
      named: [],
      onSprintFinish,
      ...(slots ? { slots } : {}),
    };

  const roles = (from.roles ?? []).filter((r) => SIGNOFF_ROLES.includes(r));
  /* No ranks and no steps is no rule — not a chain of one step asking nobody,
     which would read on screen as "a step is set up and it is broken". */
  return {
    steps: roles.length ? [{ step: '', roles }] : [],
    named: [],
    onSprintFinish,
    ...(slots ? { slots } : {}),
  };
}

/** What gets stored: the chain (or the slots), and the switch. Typed names live
 *  on the panel. `slots` is written only when the board is on the model, so its
 *  absence stays the back-compat switch that keeps a rank board on the chain. */
export function writePolicy(policy: SignoffPolicy): StoredSignoffPolicy {
  /* A stage policy is stored as its stages and nothing else of the older two
     models — its presence is the switch readPolicy reads first. */
  if (usesStages(policy))
    return { onSprintFinish: policy.onSprintFinish, stages: policy.stages };
  return {
    steps: policy.steps,
    onSprintFinish: policy.onSprintFinish,
    ...(policy.slots ? { slots: policy.slots } : {}),
  };
}

/**
 * ── DEFAULT + PER-TYPE OVERRIDES — configurable sign-offs R1 ──────────────────
 *
 * Vinnie, 30 September 2026: "Default + override any type." A board carries a
 * DEFAULT policy plus optional overrides keyed by the kind of thing being signed
 * — `'sprint'`, `'release'`, or a signable `DocumentKind`. A subject reads its
 * override if the board set one, else the default.
 *
 * ⚠️ THE PRESENCE OF `default` IS THE SWITCH, and it never rewrites stored data.
 * Every board today stores a flat `StoredSignoffPolicy`; `readConfig` reads that
 * as `{ default: <that policy>, overrides: {} }` without touching it, exactly as
 * `readPolicy` reads the old rank shape forward. The config shape appears in
 * storage only once a board actually overrides a type (see `writeConfig`), so no
 * live board's `signoff_policy` changes shape until somebody makes an override.
 */
function isStoredConfig(
  v: StoredSignoffPolicy | StoredSignoffConfig | undefined | null
): v is StoredSignoffConfig {
  return (
    !!v &&
    typeof v === 'object' &&
    'default' in v &&
    !!(v as StoredSignoffConfig).default &&
    typeof (v as StoredSignoffConfig).default === 'object'
  );
}

/** The stored value (old flat policy OR new config) normalised to `{default,
 *  overrides}`, without ever rewriting what is stored. A malformed override entry
 *  — one whose value is not an object — is dropped rather than carried, the same
 *  care `readStageMember` takes: who signs is a crown jewel. */
export function readConfig(
  stored: StoredSignoffPolicy | StoredSignoffConfig | undefined | null
): { default: StoredSignoffPolicy; overrides: Record<string, StoredSignoffPolicy> } {
  if (isStoredConfig(stored)) {
    const overrides: Record<string, StoredSignoffPolicy> = {};
    const src = stored.overrides;
    if (src && typeof src === 'object')
      for (const [key, value] of Object.entries(src))
        if (value && typeof value === 'object') overrides[key] = value as StoredSignoffPolicy;
    return { default: (stored.default ?? {}) as StoredSignoffPolicy, overrides };
  }
  return { default: (stored ?? {}) as StoredSignoffPolicy, overrides: {} };
}

/** What gets stored for a default + its overrides. A board with NO overrides is
 *  written back as the flat default alone — so a board that never overrides keeps
 *  the shape it always had, and the config shape appears only when it is earned.
 *  This is the same restraint `writePolicy` shows with `slots`/`stages`. */
export function writeConfig(config: {
  default: StoredSignoffPolicy;
  overrides: Record<string, StoredSignoffPolicy>;
}): StoredSignoffPolicy | StoredSignoffConfig {
  const keys = Object.keys(config.overrides);
  if (keys.length === 0) return config.default;
  return { default: config.default, overrides: config.overrides };
}

/**
 * THE POLICY THAT GOVERNS ONE SUBJECT TYPE — configurable sign-offs R1.
 *
 * The override for that type if the board set one, else the board default,
 * resolved through `readPolicy` into the chain the store raises against. This is
 * the one place an override is chosen; every UI call site keeps calling
 * `readPolicy(meta.signoffPolicy)` and gets the board default.
 */
export function policyForType(
  stored: StoredSignoffPolicy | StoredSignoffConfig | undefined | null,
  typeKey: string
): SignoffPolicy {
  const config = readConfig(stored);
  return readPolicy(config.overrides[typeKey] ?? config.default);
}

/** Whether the board has set an override for this type — the switch the editor
 *  reads to show "Its own stages" against "Uses the board default". */
export function hasOverride(
  stored: StoredSignoffPolicy | StoredSignoffConfig | undefined | null,
  typeKey: string
): boolean {
  return typeKey in readConfig(stored).overrides;
}

/** Everything the rule has to be read against, as the board stands right now. */
export interface TheWorld {
  /** The board's owner, as Settings has it. Half a name is not a person. */
  owner: Signatory;
  access: Access;
  roles: Roles;
  projectRoles: ProjectRoles;
  profiles: Profiles;
  projectId: string;
}

const tidy = (s: string | undefined): string => (s ?? '').trim();

/** A person is whole when there is somebody to address and something to call them. */
export const isSomebody = (s: Signatory): boolean =>
  tidy(s.name).length > 0 && tidy(s.position).length > 0;

/**
 * WHAT MAKES TWO ROWS THE SAME PERSON.
 *
 * The address when there is one, because two spellings of a name are one
 * person and an address is not ambiguous. Name and position otherwise, which
 * is the rule the roster in `signatories.ts` has always used and is the only
 * thing available for somebody with no account here.
 */
const sameAs = (s: Signatory): string =>
  tidy(s.email).toLowerCase() ||
  `${tidy(s.name).toLowerCase()}|${tidy(s.position).toLowerCase()}`;

/** One person, from an address, using whatever the workspace knows of them. */
function asSignatory(email: string, role: Role, profiles: Profiles): Signatory {
  return {
    name: displayName(email, profiles),
    position: ROLE_LABEL[role],
    email: email.toLowerCase(),
  };
}

/**
 * EVERYBODY THIS RULE ASKS FOR, IN THE ORDER THEY BELONG ON THE SHEET.
 *
 * Order is data — `seq` is part of the key on `signoffs` because a sheet is
 * read top to bottom and the order carries seniority. So this is not sorted
 * for tidiness: owner, then the board's managers, then workspace admins, then
 * anybody typed in. Within a rank, by name, so the same rule read twice gives
 * the same sheet.
 *
 * ⚠️ NAMED PEOPLE COME LAST AND STILL WIN A TIE. Somebody who is both a
 * manager here and typed into the list appears ONCE, at their rank, because
 * that is where seniority puts them — but the row that survives is the typed
 * one, since a board that bothered to write "Chief Information Officer" meant
 * that rather than "Project manager".
 */
export function whoMustSign(
  asks: { roles: SignoffRole[]; named: Signatory[] },
  world: TheWorld
): Signatory[] {
  const { access, roles, projectRoles, profiles, projectId } = world;
  const wants = new Set(asks.roles);
  const out: Signatory[] = [];

  const rankOf = (email: string): Role =>
    effectiveRole({
      email,
      admins: access.admins,
      defaults: roles,
      on: projectRoles,
      projectId,
    });

  if (wants.has('owner') && isSomebody(world.owner)) out.push(world.owner);

  const everybody = [...new Set(access.emails.map((e) => e.toLowerCase()))].sort();

  if (wants.has('manager'))
    for (const email of everybody)
      if (rankOf(email) === 'manager') out.push(asSignatory(email, 'manager', profiles));

  /*
   * ADMINS READ FROM THE ADMIN LIST, not from `rankOf`. An admin who has been
   * given a lower rank on this one board still administers the workspace, and
   * `effectiveRole` correctly reports the board rank — so asking it here would
   * quietly drop them from a rule that names admins.
   */
  if (wants.has('admin'))
    for (const email of [...new Set(access.admins.map((e) => e.toLowerCase()))].sort())
      out.push(asSignatory(email, 'admin', profiles));

  for (const who of asks.named)
    if (isSomebody(who))
      out.push({ name: tidy(who.name), position: tidy(who.position), email: tidy(who.email) });

  /*
   * ONE ROW PER PERSON, keeping the FIRST position they appear at and the LAST
   * description of them. The position in the list is seniority and the earliest
   * claim on it is the right one; the description is what somebody typed, and a
   * typed row is always more deliberate than a rank read off the access list.
   */
  const seen = new Map<string, number>();
  const rows: Signatory[] = [];
  for (const who of out) {
    const key = sameAs(who);
    const at = seen.get(key);
    if (at === undefined) {
      seen.set(key, rows.length);
      rows.push(who);
      continue;
    }
    rows[at] = { ...rows[at], position: who.position, email: rows[at].email || who.email };
  }
  return rows;
}

/**
 * ── THE WHOLE SHEET THE CHAIN PRODUCES, IN ORDER — #346 ─────────────────────
 *
 * Step order first, then seniority inside a step, which is what `seq` records
 * when the store freezes these onto the rows. Read once, at the moment of
 * asking, exactly as before — this file still knows nothing about storage.
 *
 * ⚠️ ONE ROW PER PERSON PER STEP, AND NOT PER SHEET. Somebody who is both the
 * manager and the owner of a board appears at BOTH steps and is asked twice.
 * That reads like a fault and is not one: the manager's approval and the
 * owner's approval are two different approvals, and collapsing them would let
 * one signature silently satisfy two steps somebody is separately accountable
 * for. Asking twice is the safe direction to be wrong in; a step that quietly
 * signs itself is not.
 *
 * ⚠️ TYPED NAMES GO ON THE LAST STEP THAT ASKS ANYBODY, and this is a guess
 * rather than a decision of Vinnie's. The panel exists for outsiders — a
 * client's sponsor, an executive director — and an outsider approving a
 * fortnight approves it at the end rather than in the middle. It is said out
 * loud on the settings screen instead of being left to be discovered, and it is
 * the one part of this shape to put back to him. On a chain of ONE step, which
 * is every board today, this is exactly #332's behaviour and no guess at all.
 */
export interface SheetRow {
  /**
   * WHICH SLICE OF THE SHEET THIS ROW BELONGS TO. On the legacy two models it is
   * a `SignoffStep` from the fixed vocabulary. On the stage model it is the
   * board-defined stage's id — `step` is plain TEXT on the row (migration 0129
   * drops the CHECK that used to restrict it), so a stage id rides in it the same
   * way a step name does, and the frozen order comes from `stageSeq` beside it.
   */
  step: SignoffStep | string;
  who: Signatory;
  /**
   * THE FROZEN STAGE ORDER — configurable sign-offs R1, set only on stage rows.
   *
   * A DENSE RANK of the stage this row belongs to: stages sorted by their
   * `order`, then numbered 0,1,2… with no gaps and no ties. The store freezes it
   * onto `signoffs.stage_seq` so a board editing its stages after a sheet is out
   * cannot reorder a sheet already raised, and the order gate (openStep, whyLocked
   * and the database trigger in 0129) reads `stageSeq ?? stepOrder(step)`. Absent
   * on the rank and slots models, whose rows are ordered by their step alone.
   */
  stageSeq?: number;
  /**
   * THE STAGE'S ANSWER RULE, frozen onto the row — configurable sign-offs R1. Set
   * only on stage rows whose rule is not 'all' ('anyOne' or `{quorum:N}`); the
   * store writes it to `signoffs.stage_mode` and the 0133 trigger reads it to
   * settle a stage once the rule is met. Absent means 'all' (everyone signs).
   */
  stageMode?: SignoffStageMode;
  /**
   * ALREADY SIGNED THE MOMENT IT IS RAISED — the slots model's developer row.
   * The person raising a sign-off IS the developer, and raising it is their
   * attestation; there is nothing for them to sign later. The store writes an
   * attested row with `signedAt` set at the moment of raising, so it sits in the
   * one ledger as a complete signature rather than a field on the sprint. It
   * gates nothing: step '' sorts first and a signed row never blocks another.
   * Absent on the rank model, where every row is signed by a person afterwards.
   */
  attested?: boolean;
}

export function sheetFor(policy: SignoffPolicy, world: TheWorld, developer?: Signatory): SheetRow[] {
  if (usesStages(policy)) return stagesSheet(policy, world);
  if (usesSlots(policy)) return slotsSheet(policy, world, developer);
  const out: SheetRow[] = [];
  for (const step of policy.steps)
    for (const who of whoMustSign({ roles: step.roles, named: namedAtStep(policy, step.step) }, world))
      out.push({ step: step.step, who });
  return out;
}

/**
 * ── ONE STAGE'S MEMBERS TURNED INTO PEOPLE — configurable sign-offs R1 ────────
 *
 * Each member kind resolves the way its shape demands, and all three go through
 * the SAME who-signs machinery as the older models so a stage cannot ask for
 * somebody the rest of the product could not:
 *
 *   role     — `whoMustSign` with that one rank, exactly as the rank chain reads
 *              a step: the owner from Settings, the board's managers, workspace
 *              admins, resolved against TheWorld at the moment of asking.
 *   person   — the referenced account, named from the roster (`displayName`) and
 *              positioned by the rank they hold on this board, so their current
 *              name is used and then frozen onto the row like every other.
 *   external — the typed person, verbatim: somebody with no account here.
 *
 * ⚠️ ONE ROW PER PERSON WITHIN A STAGE, and not across stages. Somebody asked at
 * two stages is asked twice — two stages are two approvals, exactly as the rank
 * chain asks a two-ranked person at two steps. Within a single stage a person
 * named two ways (a role that resolves to them, and again by account) is one row,
 * keeping the first place they appear — the same de-dupe `whoMustSign` uses.
 */
function resolveStageMembers(stage: SignoffStage, world: TheWorld): Signatory[] {
  const out: Signatory[] = [];
  for (const m of stage.members) {
    if (m.kind === 'role' && m.role) {
      out.push(...whoMustSign({ roles: [m.role], named: [] }, world));
    } else if (m.kind === 'person' && m.personRef) {
      const email = m.personRef.toLowerCase();
      const role = effectiveRole({
        email,
        admins: world.access.admins,
        defaults: world.roles as Record<string, string>,
        on: world.projectRoles as Record<string, Record<string, string>>,
        projectId: world.projectId,
      });
      out.push(asSignatory(email, role, world.profiles));
    } else if (m.kind === 'external') {
      out.push({ name: tidy(m.name), position: tidy(m.position), email: tidy(m.email) });
    }
  }

  /* One row per person, first place kept, last description kept — the same rule
     whoMustSign applies within a step. */
  const seen = new Map<string, number>();
  const rows: Signatory[] = [];
  for (const who of out) {
    if (!isSomebody(who)) continue;
    const key = sameAs(who);
    const at = seen.get(key);
    if (at === undefined) {
      seen.set(key, rows.length);
      rows.push(who);
    } else {
      rows[at] = { ...rows[at], position: who.position, email: rows[at].email || who.email };
    }
  }
  return rows;
}

/**
 * ── THE WHOLE SHEET A STAGE POLICY PRODUCES, IN ORDER — configurable sign-offs R1 ─
 *
 * Stages sorted by their `order`, then a DENSE RANK (0,1,2… no gaps, no ties)
 * frozen onto each row as `stageSeq` — the number the store writes to
 * `signoffs.stage_seq` and the order gate reads. Within a stage, members in the
 * order `resolveStageMembers` returns them.
 *
 * ⚠️ `seq` STAYS GLOBALLY UNIQUE PER SUBJECT PER ROUND, ACROSS STAGES, and this
 * is where that has to be got right. The store freezes the row's position in THIS
 * list as `seq`, and the partial unique indexes (0045 documents, 0075 sprints,
 * tightened in 0096) key a live row by (tenant, board, subject, seq). If `seq`
 * restarted at 0 inside each stage, two rows in different stages would collide on
 * it and the raise would fail. So order carries two numbers that mean different
 * things: `stageSeq` is which stage (and gates the chain), `seq` is the row's one
 * place on the whole sheet (and keys the row). This function emits rows in the
 * final sheet order so the store's running `seq` is exactly that global position.
 */
export function stagesSheet(policy: SignoffPolicy, world: TheWorld): SheetRow[] {
  const stages = [...(policy.stages ?? [])].sort((a, b) => a.order - b.order);
  const out: SheetRow[] = [];
  stages.forEach((stage, denseRank) => {
    const people = resolveStageMembers(stage, world);
    /* Freeze the rule against the PEOPLE the stage actually resolved to (not the
       member entries), since those are the rows that get signed — a quorum of
       two means two of the real rows. Clamped exactly as readStageMode clamps.
       Only carried when it is not 'all' (the default the store writes nothing for). */
    const mode = readStageMode(stage.mode, people.length);
    for (const who of people)
      out.push({ step: stage.id, who, stageSeq: denseRank, ...(mode !== 'all' ? { stageMode: mode } : {}) });
  });
  return out;
}

/**
 * ── WHO SIGNS, AS NAMED SLOTS RATHER THAN RANK BUCKETS — the redesign ────────
 *
 * The rank chain above asked EVERYONE who held a rank, so a workspace admin — a
 * permission, not an approver — landed on the sheet unasked, and the list grew
 * with the team (Vinnie, 6 September 2026, on a sheet that had reached 13). The
 * slots model asks named people only, and no rank ever adds anybody:
 *
 *   developer — the person raising it, attested by the act of raising (step '',
 *               written already-signed; see SheetRow.attested).
 *   uat       — the tester, or a whole team of them: the named people tagged to
 *               the 'uat' step. More than one is the point — Vinnie asked for it.
 *   approver  — the one manager who approves: the named person tagged 'manager'.
 *               "not all IT managers have to sign, just 1" — so it is a name, not
 *               the manager rank, even when that person is also a board admin.
 *   owner     — the board's owner, optional, last: the owner fields, step 'owner'.
 *
 * ⚠️ THE SLOTS ARE FILLED FROM THE SAME PANEL, not a second list. UAT and
 * approver are `meta.signatories` tagged with a step (`Signatory.step`), exactly
 * the tags the rank chain already used; the owner is the owner fields. The
 * `slots` booleans say which slots this board uses — turning one off leaves its
 * people off the sheet without deleting them from the panel.
 */
export function usesSlots(policy: Pick<SignoffPolicy, 'slots'>): boolean {
  return !!policy.slots;
}

/**
 * WHETHER A BOARD IS ON THE STAGE MODEL — configurable sign-offs R1.
 *
 * Mirrors `usesSlots`: presence of the `stages` array is the switch. A policy
 * that carries stages is resolved and ordered the stage way; one that does not
 * keeps whichever of the older two models it has. An empty stages array still
 * counts as "on" so a board that has switched on but defined nothing is told its
 * rule is empty in stage terms, rather than silently read as a rank board.
 */
export function usesStages(policy: Pick<SignoffPolicy, 'stages'>): boolean {
  return !!policy.stages;
}

/** Which chain step each slot's rows carry. Developer is '' — first, and signed.
 *  The UI reads this to tag a named person to the slot they answer. */
export const SLOT_STEP: Record<keyof SignoffSlots, SignoffStep> = {
  developer: '',
  uat: 'uat',
  approver: 'manager',
  owner: 'owner',
};

/** The slots in the order they sit on the sheet. */
export const SLOT_ORDER: (keyof SignoffSlots)[] = ['developer', 'uat', 'approver', 'owner'];

export const SLOT_LABEL: Record<keyof SignoffSlots, string> = {
  developer: 'Developer',
  uat: 'UAT tester',
  approver: 'Approver',
  owner: 'Project owner',
};

export const SLOT_BLURB: Record<keyof SignoffSlots, string> = {
  developer: 'The person who raises the sign-off. They sign by raising it — nothing to chase.',
  uat: 'Whoever tested it. Name one, or a whole team — each signs their own row.',
  approver: 'The one manager who approves it. A name, not everyone who holds the rank.',
  owner: 'The last word before it goes live. Optional. Read from the owner set above.',
};

export function slotsSheet(policy: SignoffPolicy, world: TheWorld, developer?: Signatory): SheetRow[] {
  const slots = policy.slots;
  if (!slots) return [];
  const out: SheetRow[] = [];

  /* The developer, attested. Only when there is somebody to attest — a preview
     before anyone has raised it, or a raise with no signed-in person, simply
     shows no developer row rather than an empty one. */
  if (slots.developer && developer && isSomebody(developer))
    out.push({ step: SLOT_STEP.developer, who: developer, attested: true });

  const taggedFor = (step: SignoffStep): Signatory[] =>
    named(policy)
      .filter((who) => (who.step ?? '') === step)
      .map((who) => ({ name: tidy(who.name), position: tidy(who.position), email: tidy(who.email) }));

  if (slots.uat) for (const who of taggedFor(SLOT_STEP.uat)) out.push({ step: SLOT_STEP.uat, who });
  if (slots.approver)
    for (const who of taggedFor(SLOT_STEP.approver)) out.push({ step: SLOT_STEP.approver, who });
  if (slots.owner && isSomebody(world.owner)) out.push({ step: SLOT_STEP.owner, who: world.owner });

  return out;
}

const named = (policy: SignoffPolicy): Signatory[] => policy.named.filter(isSomebody);

/**
 * THE TYPED PEOPLE ASKED AT ONE STEP — #352.
 *
 * ⚠️ AND THE OLD ANSWER WAS A GUESS THAT TURNED OUT WRONG. [[#346]] put every
 * typed name on the LAST step, reasoning that somebody outside the company
 * approves the finished thing rather than a stage of it. Vinnie found the
 * counter-example by looking at the screen — a named TESTER, who is the whole
 * point of the first step and will be the commonest typed name there is. A
 * default that is wrong for the commonest case is not a default.
 *
 * So each person says which step they answer, and nobody is asked by accident.
 * On a board with no chain the question does not arise: there is one approval,
 * and everybody on the panel is asked at it exactly as before.
 */
export function namedAtStep(policy: SignoffPolicy, step: SignoffStep): Signatory[] {
  if (isPlain(policy)) return named(policy);
  return named(policy).filter((who) => (who.step ?? '') === step);
}

/** A board with no chain: one step, and it has no name. */
export const isPlain = (policy: SignoffPolicy): boolean =>
  policy.steps.length <= 1 && policy.steps.every((s) => s.step === '');

/**
 * TYPED NAMES THAT NO STEP ASKS — #352.
 *
 * On a chain, somebody with no step set is asked nothing. That is the honest
 * behaviour and it is the one that must be SAID: silently folding them into a
 * step is how the tester ended up on the owner's step in the first place, and
 * silently dropping them is worse.
 */
export function unassignedNames(policy: SignoffPolicy): Signatory[] {
  if (isPlain(policy)) return [];
  const steps = new Set(policy.steps.map((s) => s.step));
  return named(policy).filter((who) => !steps.has(who.step ?? ('' as SignoffStep)));
}

/**
 * WHY THIS RULE CANNOT BE USED, in the words to put on the screen.
 *
 * Empty means it can. Checked before a sign-off is raised AND offered on the
 * settings screen, so a board is told its rule is empty while somebody is
 * looking at it rather than at the moment a sprint finishes.
 */
export function policyProblem(policy: SignoffPolicy, world: TheWorld): string {
  if (usesStages(policy)) return stagesProblem(policy, world);
  if (usesSlots(policy)) return slotsProblem(policy, world);
  const ranks = policy.steps.flatMap((s) => s.roles);
  if (policy.steps.length === 0 || (ranks.length === 0 && named(policy).length === 0))
    return 'Nobody is set to sign work off here. Choose who signs, or add somebody by name.';
  const rows = sheetFor(policy, world);
  if (rows.length === 0) {
    if (ranks.includes('owner') && !isSomebody(world.owner))
      return 'The rule asks the project owner to sign, and this board has no owner set. Set one in Settings, or choose somebody else.';
    return 'Nobody currently matches the rule, so there is nobody to ask. Check who signs.';
  }
  /*
   * A STEP THAT ASKS NOBODY IS A BREAK IN THE CHAIN, and it has to be said out
   * loud rather than skipped over. Skipping it silently is how a board ends up
   * believing the manager approved something the manager was never sent.
   */
  const empty = policy.steps.find((s) => rows.every((r) => r.step !== s.step));
  if (empty)
    return `${SIGNOFF_STEP_LABEL[empty.step]} is switched on and asks nobody. Choose who answers it, or turn that step off.`;
  /*
   * AND SO IS A PERSON NO STEP ASKS — #352. On a chain, somebody typed into the
   * panel with no step against them signs nothing. The board is looking at
   * their name and would reasonably believe otherwise.
   */
  const adrift = unassignedNames(policy);
  if (adrift.length)
    return `${adrift.map((w) => w.name).join(', ')} ${adrift.length > 1 ? 'are' : 'is'} on the list of people who sign, but no step asks ${adrift.length > 1 ? 'them' : 'them'}. Say which step, or take the name off.`;
  return '';
}

/**
 * WHY A SLOTS RULE CANNOT BE USED, in the words to put on the screen.
 *
 * Empty means it can. The developer signs by raising and is attested, so a board
 * where the developer is the ONLY slot on would approve itself the instant it is
 * raised — said out loud, because a self-approving sign-off is not evidence of
 * anything and is the exact shape this redesign exists to stop.
 */
function slotsProblem(policy: SignoffPolicy, world: TheWorld): string {
  const slots = policy.slots ?? NO_SLOTS;
  const on = Object.values(slots).some(Boolean);
  if (!on)
    return 'Nobody is set to sign work off here. Turn on who signs — the developer, a tester, an approver, the owner.';

  const tagged = (step: SignoffStep) => named(policy).filter((who) => (who.step ?? '') === step);
  if (slots.uat && tagged('uat').length === 0)
    return 'UAT sign-off is on but no tester is named. Add one in the panel above and set it to UAT — a team of them is fine.';
  if (slots.approver && tagged('manager').length === 0)
    return 'An approver is asked for but nobody is named. Add one in the panel above and set it to Approver.';
  if (slots.owner && !isSomebody(world.owner))
    return 'The owner is set to sign but this board has no owner. Set one further up this page, or turn the owner off.';

  const signable = slotsSheet(policy, world).filter((r) => !r.attested);
  if (signable.length === 0)
    return 'Only the developer would sign, so it would approve itself the moment it is raised. Add a tester, an approver or the owner.';
  return '';
}

/**
 * WHY A STAGE RULE CANNOT BE USED, in the words to put on the screen —
 * configurable sign-offs R1.
 *
 * Empty means it can. The guards mirror the rank chain's and the slots model's,
 * in the same plain voice, and each is a way a stage sheet could look set up while
 * asking nobody — the exact failure a sign-off must never have:
 *
 *   NO STAGES AT ALL — the board switched to stages and defined none.
 *   A STAGE THAT NAMES NOBODY — a stage with an empty member list. It asks
 *     nobody, and an 'any one of them' stage with no members is the sharp case:
 *     'any of nobody' would pass on no signatures, so it is refused out loud.
 *   NOTHING REQUIRED — every stage is optional, so nothing has to be signed and
 *     the sheet would approve itself, like a slots board with only the developer.
 *   A STAGE THAT RESOLVES TO NOBODY — members are named but none of them is a
 *     person right now (a role that matches nobody, e.g. the owner on a board with
 *     no owner). The same silent break the rank chain guards against, found the
 *     same way: a stage that survives to the sheet with no rows on it.
 */
function stagesProblem(policy: SignoffPolicy, world: TheWorld): string {
  const stages = policy.stages ?? [];
  if (stages.length === 0)
    return 'Nobody is set to sign work off here. Add a stage and say who signs it.';

  /* A name is required (the field is marked so), and without one a raised sheet
     can only call the stage "Sign-off" — ambiguous the moment there are two. Ask
     for it before the who-signs problems, so a freshly added stage reads its
     first nudge against the name field it opens with. */
  if (stages.some((s) => !s.label.trim()))
    return 'Give every stage a name — name the new one, or remove it.';

  /* Named, so it can be named in the warning — never the raw id. */
  const nameOf = (s: SignoffStage) => `The "${s.label.trim()}"`;

  const emptyMembers = stages.find((s) => s.members.length === 0);
  if (emptyMembers)
    return `${nameOf(emptyMembers)} stage asks nobody. Add who answers it, or remove the stage.`;

  if (stages.every((s) => s.optional))
    return 'Every stage here is optional, so nothing has to be signed. Make at least one stage required, or add a stage that is.';

  const rows = stagesSheet(policy, world);
  if (rows.length === 0)
    return 'Nobody currently matches the stages, so there is nobody to ask. Check who each stage asks.';

  const blank = stages.find((s) => rows.every((r) => r.step !== s.id));
  if (blank)
    return `${nameOf(blank)} stage is set up but asks nobody right now. Check who it asks, or remove the stage.`;

  return '';
}

/**
 * ── WHICH STEP MAY BE ANSWERED RIGHT NOW — #346 ─────────────────────────────
 *
 * The chain's whole purpose. Vinnie, 4 September 2026: the tester signs off,
 * then the manager, then the project owner. Asked all at once those are three
 * signatures in any order, and the project owner can approve a fortnight before
 * anybody has tested it.
 *
 * ⚠️ THE ORDER IS IN THE STEP, NOT IN THE BOARD'S RULE. `SIGNOFF_STEPS` is a
 * fixed list in the order work is approved, so a sheet can be put in order from
 * the rows alone — without reading the board's policy, which may have been
 * edited since, and without reading `seq`, which is seniority inside a step and
 * says nothing across steps. It is what lets the database enforce this too.
 *
 * Null means nothing may be answered: either every row is in, or somebody has
 * said no and the answer is settled whatever the rest would have said.
 */
const stepOrder = (s: SignoffStep | string): number =>
  s === '' ? -1 : (SIGNOFF_STEPS as readonly string[]).indexOf(s);

/**
 * WHAT ORDERS A ROW ON THE SHEET — configurable sign-offs R1.
 *
 * The frozen stage rank when the row carries one, else the legacy step order.
 * A raised sheet is all one model, so `stageSeq` is set on every row or on none
 * within a subject — the two ordering spaces never mix on one sheet. For a legacy
 * row (no `stageSeq`) this is byte-for-byte `stepOrder(step)`, exactly as before.
 */
type Answered = { step?: SignoffStep | string; stageSeq?: number | null; signedAt?: number | null; answer?: string };

const orderKey = (r: Answered): number => r.stageSeq ?? stepOrder(r.step ?? '');

export function openStep(rows: Answered[]): (SignoffStep | string) | null {
  /* A refusal ends it. Vinnie, 3 September: one no decides it without waiting
     for the rest — so asking the next step would be asking about something
     already settled. */
  if (rows.some((r) => r.signedAt && r.answer === 'rejected')) return null;
  /* One ordering key per distinct step/stage id — all rows of a stage share its
     stageSeq, so the first row seen fixes the key for that slice. */
  const keyByStep = new Map<string, number>();
  for (const r of rows) {
    const s = r.step ?? '';
    if (!keyByStep.has(s)) keyByStep.set(s, orderKey(r));
  }
  const steps = [...keyByStep.keys()].sort((a, b) => keyByStep.get(a)! - keyByStep.get(b)!);
  for (const s of steps)
    if (rows.some((r) => (r.step ?? '') === s && !r.signedAt)) return s;
  return null;
}

/**
 * WHY THIS STEP CANNOT BE ANSWERED YET, in the words to put on the screen.
 *
 * Empty means it can. Said rather than merely enforced: a row with its buttons
 * quietly missing is a bug to whoever is looking at it, and the one thing this
 * feature cannot afford is somebody concluding the sheet is broken and going
 * round it.
 */
export function whyLocked(
  step: SignoffStep | string,
  rows: Answered[],
  policy?: StoredSignoffPolicy | StoredSignoffConfig | null
): string {
  if (rows.some((r) => r.signedAt && r.answer === 'rejected'))
    return 'Somebody has said no, so nothing further is asked.';
  const open = openStep(rows);
  if (open === null) return '';
  /* Compare by the same ordering key openStep uses — the frozen stage rank on a
     stage sheet, the step order on a legacy one. */
  const keyOf = (s: SignoffStep | string): number => {
    const row = rows.find((r) => (r.step ?? '') === s);
    return row ? orderKey(row) : stepOrder(s);
  };
  if (keyOf(step) <= keyOf(open)) return '';
  /* Name the blocking slice. A legacy step is named from the fixed vocabulary. A
     stage is named by its resolved label — configurable sign-offs R1 — once the
     caller passes the policy through; without one (the two-argument form the unit
     tests and any label-less caller use) it keeps the generic "An earlier stage".*/
  const openIsStage = rows.some((r) => (r.step ?? '') === open && r.stageSeq != null);
  const label = openIsStage
    ? policy === undefined
      ? 'An earlier stage'
      : stepLabel(open, policy)
    : SIGNOFF_STEP_LABEL[open as SignoffStep];
  return `${label} has to answer first.`;
}

/**
 * TWO ROWS THAT BELONG TO THE SAME SHEET.
 *
 * Exactly one of the three is set on any row — the database refuses both and
 * neither, see 0075 — so this is an equality on all three rather than a guess
 * at which one matters.
 */
export function sameSubject(a: Subject, b: Subject): boolean {
  return (
    (a.sprintId ?? '') === (b.sprintId ?? '') &&
    (a.build ?? null) === (b.build ?? null) &&
    (a.documentId ?? '') === (b.documentId ?? '')
  );
}

interface Subject {
  sprintId?: string;
  build?: number;
  documentId?: string;
}

/**
 * ── WHEN IS THE THING ACTUALLY APPROVED ─────────────────────────────────────
 *
 * EVERY named person, decided by Vinnie on 3 September 2026 over "any one of
 * them". His reasoning is the card's: a sheet showing two unsigned rows that
 * still read as approved is how a sign-off stops being evidence. It also keeps
 * one word meaning one thing across every board, which is what lets a report
 * add them up.
 *
 * ⚠️ A SINGLE REFUSAL DECIDES IT, and immediately. Waiting for the rest before
 * saying "rejected" would leave a thing that cannot possibly be approved
 * sitting in the waiting list, and somebody would chase the remaining
 * signatures for an answer that is already known.
 */
export type SignoffState = 'waiting' | 'approved' | 'rejected';

export function stateOf(rows: { signedAt?: number | null; answer?: string }[]): SignoffState {
  if (rows.length === 0) return 'waiting';
  if (rows.some((r) => r.signedAt && r.answer === 'rejected')) return 'rejected';
  return rows.every((r) => r.signedAt) ? 'approved' : 'waiting';
}

/** How far along, for a line that says so without anybody opening it. */
export function howFar(rows: { signedAt?: number | null }[]): { signed: number; of: number } {
  return { signed: rows.filter((r) => r.signedAt).length, of: rows.length };
}

/**
 * HOW MANY SUBJECTS ARE STILL WAITING ON A SIGNATURE — the number the Sign-offs
 * inbox shows as open. It badges the nav item and decides whether the item is
 * shown at all: a standing, usually-empty section is clutter, so it appears only
 * while this is above zero (or while you are on it). Grouped exactly as the
 * inbox groups (#332) — the three subject kinds are mutually exclusive on a row.
 */
export function outstandingSignoffCount(
  rows: {
    sprintId?: string | null;
    build?: number | null;
    documentId?: string | null;
    signedAt?: number | null;
    answer?: string;
  }[]
): number {
  const by = new Map<string, typeof rows>();
  for (const r of rows) {
    const key = r.sprintId
      ? `s:${r.sprintId}`
      : r.build != null
        ? `b:${r.build}`
        : r.documentId
          ? `d:${r.documentId}`
          : '';
    if (!key) continue;
    const g = by.get(key);
    if (g) g.push(r);
    else by.set(key, [r]);
  }
  let n = 0;
  for (const g of by.values()) if (stateOf(g) === 'waiting') n += 1;
  return n;
}
